import { c as _c } from "react/compiler-runtime";
import { Box, Card, CardContent, CardHeader, Skeleton, Table, TableCell, TableContainer, TableRow, Typography, styled, type CardProps, type SxProps, type Theme } from '@mui/material';
import { Provider, type SourceAccount } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Tag } from '@/components/tag';
import { TagsElasticContainer } from '@/components/tags/tagsElasticContainer';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import { NativeVaultColor } from '@/theme/colorSchemes';
import { useVaultDetailsDialog } from './useVaultDetailsDialog';
import { ConfirmationDialog } from '../dialogs/confirmationDialog';
import { OptionMenu } from '../option-menu/option-menu';
export const VaultCard = t0 => {
  const $ = _c(18);
  let children;
  let clickable;
  let props;
  let selected;
  let sx;
  if ($[0] !== t0) {
    const {
      clickable: t1,
      selected: t2,
      children: t3,
      data,
      editable,
      onChange,
      onExternalOpenDialog,
      vaultAccount,
      sx: t4,
      ...t5
    } = t0;
    clickable = t1;
    selected = t2;
    children = t3;
    sx = t4;
    props = t5;
    $[0] = t0;
    $[1] = children;
    $[2] = clickable;
    $[3] = props;
    $[4] = selected;
    $[5] = sx;
  } else {
    children = $[1];
    clickable = $[2];
    props = $[3];
    selected = $[4];
    sx = $[5];
  }
  let t1;
  if ($[6] !== clickable || $[7] !== selected) {
    t1 = theme => ({
      backgroundColor: "var(--mui-palette-background-primary)",
      ...theme.typography.body2,
      textAlign: "left",
      minWidth: "300px",
      maxWidth: "420px",
      minHeight: "172px",
      boxShadow: "none",
      padding: "24px",
      outline: selected ? "3px solid var(--mui-palette-text-primary)" : clickable ? "1px solid transparent" : "none",
      borderRadius: "16px",
      "& .MuiTypography-root": {
        fontSize: "12px",
        color: "var(--mui-palette-custom-BLACK-01)"
      },
      "& .MuiCardHeader-root": {
        padding: 0,
        marginBottom: "12px"
      },
      "& .MuiCardContent-root": {
        padding: 0
      },
      cursor: clickable ? "pointer" : "default",
      "&:hover": {
        outline: selected ? "3px solid var(--mui-palette-text-primary)" : clickable ? "1px solid var(--mui-palette-primary-main)" : "none"
      }
    });
    $[6] = clickable;
    $[7] = selected;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== sx) {
    t2 = Array.isArray(sx) ? sx : [sx];
    $[9] = sx;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t1 || $[12] !== t2) {
    t3 = [t1, ...t2];
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== children || $[15] !== props || $[16] !== t3) {
    t4 = <Card {...props} sx={t3}>{children}</Card>;
    $[14] = children;
    $[15] = props;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
};
const VaultTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  color: 'var(--mui-palette-text-secondary)',
  padding: '4px 10px 4px 0px'
}));
const VaultTablePropCell = styled(VaultTableCell)(() => {
  return {
    minWidth: '120px',
    '& p': {
      fontWeight: '500'
    }
  };
});
export const VaultCardAsAccount = t0 => {
  const $ = _c(16);
  const {
    cloudProvider,
    region,
    sx,
    loading,
    onExternalOpenDialog
  } = t0;
  const accountLabel = cloudProvider === Provider.Azure ? "subscription" : cloudProvider === Provider.Gcp ? "project" : "account";
  const title = `Source ${accountLabel}`;
  let t1;
  if ($[0] !== sx) {
    t1 = {
      backgroundColor: NativeVaultColor,
      ...sx
    };
    $[0] = sx;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onExternalOpenDialog) {
    t2 = onExternalOpenDialog ? <OptionMenu options={[{
      id: "see-details",
      text: "See vault details",
      menuItemProps: {
        onClick: onExternalOpenDialog
      }
    }]} /> : null;
    $[2] = onExternalOpenDialog;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== loading || $[5] !== t2 || $[6] !== title) {
    t3 = <VaultCardHeader loading={loading} title={title} action={t2} />;
    $[4] = loading;
    $[5] = t2;
    $[6] = title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== cloudProvider || $[9] !== loading || $[10] !== region) {
    t4 = <VaultCardContent loading={loading} cloudProvider={cloudProvider} type="provider-managed" region={region} />;
    $[8] = cloudProvider;
    $[9] = loading;
    $[10] = region;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t3 || $[14] !== t4) {
    t5 = <VaultCard editable={false} sx={t1} clickable={false}>{t3}{t4}</VaultCard>;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export function BackupVaultCard(props) {
  const $ = _c(49);
  const {
    EnableCspAssistedImmutability
  } = useFeatureFlags();
  const vaultDetailsDialog = useVaultDetailsDialog(props.loading);
  const [deleteVaultDialogOpen, setDeleteVaultDialogOpen] = useState(false);
  let t0;
  if ($[0] !== props.data || $[1] !== props.editable || $[2] !== props.onChange || $[3] !== vaultDetailsDialog) {
    t0 = () => {
      if (props.data) {
        vaultDetailsDialog.open(props.data, {
          editable: props.editable,
          onSaveSuccess: props.onChange
        });
      }
    };
    $[0] = props.data;
    $[1] = props.editable;
    $[2] = props.onChange;
    $[3] = vaultDetailsDialog;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const handleOpenDetails = t0;
  let t1;
  if ($[5] !== props.data?.status) {
    t1 = props.data?.status === "CREATING" ? {
      content: "Creating",
      variant: "secondary" as const
    } : props.data?.status === "CREATION_FAILED" ? {
      content: "Creation failed",
      variant: "error" as const
    } : undefined;
    $[5] = props.data?.status;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const statusTag = t1;
  const id = props.loading ? "loading-vault-card" : props.selected ? "backup-vault-card-selected" : "backup-vault-card";
  const t2 = props.onClick !== undefined;
  let t3;
  if ($[7] !== props) {
    t3 = () => props.onClick?.(props.data);
    $[7] = props;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = props?.data?.backgroundColor;
  let t5;
  if ($[9] !== props.sx || $[10] !== t4) {
    t5 = {
      backgroundColor: t4,
      ...props.sx
    };
    $[9] = props.sx;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = props.data?.name || "";
  const t7 = props.data?.id ?? "";
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setDeleteVaultDialogOpen(true);
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== handleOpenDetails || $[14] !== t7) {
    t9 = <VaultCardOptionMenu vaultId={t7} onEdit={handleOpenDetails} onDelete={t8} />;
    $[13] = handleOpenDetails;
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== props.loading || $[17] !== t6 || $[18] !== t9) {
    t10 = <VaultCardHeader loading={props.loading} title={t6} action={t9} />;
    $[16] = props.loading;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== statusTag) {
    t11 = statusTag && <Box data-testid="vault-status-badge" sx={{
      marginBottom: "12px"
    }}><Tag variant={statusTag.variant} content={statusTag.content} noWrap={true} /></Box>;
    $[20] = statusTag;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== EnableCspAssistedImmutability || $[23] !== props.data?.enableCspAssistedImmutability) {
    t12 = EnableCspAssistedImmutability && props.data?.enableCspAssistedImmutability && <Box data-testid="csp-assisted-immutability-badge" sx={{
      marginBottom: "12px"
    }}><Tag variant="secondary" content="CSP assisted immutability" noWrap={true} /></Box>;
    $[22] = EnableCspAssistedImmutability;
    $[23] = props.data?.enableCspAssistedImmutability;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = props.data?.vaultAttributes.cloudProvider || "";
  const t14 = props.data?.isManagedByEon ? "eon-managed" : "self-managed";
  const t15 = props.data?.region || "";
  const t16 = props.data?.tags;
  let t17;
  if ($[25] !== props.loading || $[26] !== t13 || $[27] !== t14 || $[28] !== t15 || $[29] !== t16) {
    t17 = <VaultCardContent loading={props.loading} cloudProvider={t13} type={t14} region={t15} tags={t16} />;
    $[25] = props.loading;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== id || $[32] !== props.editable || $[33] !== props.selected || $[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t17 || $[38] !== t2 || $[39] !== t3 || $[40] !== t5) {
    t18 = <VaultCard editable={props.editable} selected={props.selected} clickable={t2} onClick={t3} data-testid={id} sx={t5}>{t10}{t11}{t12}{t17}</VaultCard>;
    $[31] = id;
    $[32] = props.editable;
    $[33] = props.selected;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t17;
    $[38] = t2;
    $[39] = t3;
    $[40] = t5;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== deleteVaultDialogOpen || $[43] !== props.data?.id || $[44] !== props.onChange) {
    t19 = deleteVaultDialogOpen && <DeleteVaultDialog vaultId={props.data?.id ?? ""} onClose={() => setDeleteVaultDialogOpen(false)} onDeleted={props.onChange} />;
    $[42] = deleteVaultDialogOpen;
    $[43] = props.data?.id;
    $[44] = props.onChange;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t18 || $[47] !== t19) {
    t20 = <>{t18}{t19}</>;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
}
const DeleteVaultDialog = t0 => {
  const $ = _c(8);
  const {
    vaultId,
    onClose,
    onDeleted
  } = t0;
  const dal = useDAL();
  let t1;
  if ($[0] !== dal.vaults || $[1] !== onClose || $[2] !== onDeleted || $[3] !== vaultId) {
    t1 = async () => {
      await dal.vaults.delete(vaultId);
      onDeleted?.();
      onClose();
    };
    $[0] = dal.vaults;
    $[1] = onClose;
    $[2] = onDeleted;
    $[3] = vaultId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const onConfirm = t1;
  let t2;
  if ($[5] !== onClose || $[6] !== onConfirm) {
    t2 = <ConfirmationDialog isOpen={true} title="Delete Vault" description="Deleting this vault will permanently remove it and it will no longer be available to store your data. You can create a new vault in this region at any time." onConfirm={onConfirm} buttonTitle="Delete" confirmSnackbarText="Vault deleted successfully." onCancel={onClose} />;
    $[5] = onClose;
    $[6] = onConfirm;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const VaultCardOptionMenu = t0 => {
  const $ = _c(13);
  const {
    vaultId,
    onEdit,
    onDelete
  } = t0;
  const dal = useDAL();
  let t1;
  if ($[0] !== onEdit) {
    t1 = {
      id: "edit-vault",
      text: "See vault details",
      menuItemProps: {
        onClick: onEdit
      }
    };
    $[0] = onEdit;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== dal || $[3] !== vaultId) {
    t2 = async () => {
      const res = await dal.vaults.isVaultDeletable(vaultId);
      return {
        disabled: !res.deletable,
        tooltip: res.reasons?.[0]
      };
    };
    $[2] = dal;
    $[3] = vaultId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onDelete) {
    t3 = {
      onClick: onDelete
    };
    $[5] = onDelete;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = {
      id: "delete-vault",
      text: "Delete vault",
      isDisabled: t2,
      menuItemProps: t3
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t4) {
    t5 = <OptionMenu options={[t1, t4]} />;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
export const VaultCardHeader = t0 => {
  const $ = _c(6);
  const {
    title,
    action,
    loading
  } = t0;
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CardHeader component={Skeleton} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      paddingBottom: "5px"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      title: {
        variant: "body1",
        sx: {
          fontSize: "12px",
          fontWeight: "bold"
        }
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== action || $[4] !== title) {
    t3 = <CardHeader sx={t1} title={title} slotProps={t2} action={action} />;
    $[3] = action;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export const VaultCardContent = t0 => {
  const $ = _c(20);
  const {
    cloudProvider,
    type,
    region,
    loading,
    accountId,
    tags
  } = t0;
  const {
    PolicyAndVaultMetadataLabels: t1
  } = useFeatureFlags();
  const PolicyAndVaultMetadataLabels = t1 === undefined ? false : t1;
  if (loading) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <CardContent><table className="w-full table-fixed"><tbody>{[...Array(4)].map(_temp)}</tbody></table></CardContent>;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <VaultTablePropCell><Typography>Cloud</Typography></VaultTablePropCell>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = CloudProviders[cloudProvider]?.displayName;
  let t4;
  if ($[2] !== t3) {
    t4 = <TableRow>{t2}<VaultTableCell><Typography>{t3}</Typography></VaultTableCell></TableRow>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== type) {
    t5 = type && <TableRow><VaultTablePropCell><Typography>Type</Typography></VaultTablePropCell><VaultTableCell><Typography className="truncate">{type === "eon-managed" ? "Eon-managed" : type === "provider-managed" ? "Provider-managed" : "Self-managed"}</Typography></VaultTableCell></TableRow>;
    $[4] = type;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== accountId) {
    t6 = accountId && <TableRow><VaultTablePropCell><Typography>AWS account</Typography></VaultTablePropCell><VaultTableCell><Typography>{accountId}</Typography></VaultTableCell></TableRow>;
    $[6] = accountId;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <VaultTablePropCell><Typography>Region</Typography></VaultTablePropCell>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = region || "--";
  let t9;
  if ($[9] !== t8) {
    t9 = <TableRow>{t7}<VaultTableCell><Typography>{t8}</Typography></VaultTableCell></TableRow>;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== PolicyAndVaultMetadataLabels || $[12] !== tags) {
    t10 = PolicyAndVaultMetadataLabels && tags && !!Object.keys(tags).length && <TableRow><VaultTablePropCell><Typography>Tags</Typography></VaultTablePropCell><VaultTableCell><TagsElasticContainer tags={tags} /></VaultTableCell></TableRow>;
    $[11] = PolicyAndVaultMetadataLabels;
    $[12] = tags;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t9) {
    t11 = <CardContent><TableContainer><Table className="table-fixed"><tbody>{t4}{t5}{t6}{t9}{t10}</tbody></Table></TableContainer></CardContent>;
    $[14] = t10;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};
function _temp(_, index) {
  return <tr key={index}><td className="px-[5px] pt-[5px] pl-0"><Skeleton /></td><td className="pl-[5px] pt-[5px] pr-0"><Skeleton /></td></tr>;
}